import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { darken, lighten, transparentize } from 'polished';
import { SwatchesPicker } from 'react-color';
import Theme from 'utils/theme';
import { Box, Button } from '@flexisaf/flexibull2';

export const DateLine = styled.div`
  display: inline-block;
  white-space: nowrap;
`;

export const LabelBoundry = styled.div`
  border-radius: ${(props) => props.theme.SecondaryRadius};
  background: ${(props) =>
    props.color ? props.color : props.theme.PrimaryColor};
  overflow: hidden;
  position: relative;

  & .liner {
    height: 150px;
    width: 100%;
    border-radius: ${(props) => props.theme.SecondaryRadius};
    transform-origin: 0 100%;
    z-index: 1;
    position: absolute;
    top: -50px;
    left: -50px;
    opacity: 0.5;
    box-shadow: 1px 2px 8px rgba(0, 0, 0, 0.1);
    &:nth-child(1) {
      transform: rotateZ(12deg);
      background-color: ${(props) =>
        lighten(0.06, props.color ? props.color : props.theme.PrimaryColor)};
      z-index: 1;
    }
    &:nth-child(2) {
      transform: rotateZ(-2deg);
      background-color: ${(props) =>
        lighten(0.08, props.color ? props.color : props.theme.PrimaryColor)};
      z-index: 2;
    }
    &:nth-child(3) {
      transform: rotateZ(-8deg);
      background-color: ${(props) =>
        lighten(0.12, props.color ? props.color : props.theme.PrimaryColor)};
      z-index: 3;
    }
  }
  & .loner {
    padding: 30px;
    min-height: 100px;
    position: relative;
    z-index: 4;
    background: rgba(0, 0, 0, 0.2);
  }
`;

export const ItemBanner = (props) => (
  <LabelBoundry {...props}>
    <div className="loner">{props.children}</div>
    <div className="liner" />
    <div className="liner" />
    <div className="liner" />
  </LabelBoundry>
);

ItemBanner.propTypes = {
  children: PropTypes.node,
};

const ColorWrapper = styled.div`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  & .colorshow {
    position: absolute;
    display: inline-block;
    top: ${(props) => parseInt(props.size) * 1.6}px;
    right: 0;
    z-index: 10;
  }
`;
const MiniSelected = styled.div`
  display: inline-flex;
  vertical-align: bottom;
  margin: 0 50px 0 0;
  width: ${(props) => parseInt(props.size) * 1.5}px;
  height: ${(props) => parseInt(props.size) * 1.5}px;
  border-radius: ${(props) => props.theme.PrimaryRadius};
  background: ${(props) => props.color};
  border: 2px solid ${(props) => darken(0.15, props.color)};
`;

const MiniColor = styled.div`
  display: inline-flex;
  align-items: center;
  vertical-align: bottom;
  justify-content: center;
  margin: 3px;
  width: ${(props) => parseInt(props.size) * 1.2}px;
  height: ${(props) => props.size};
  border-radius: ${(props) => props.theme.PrimaryRadius};
  background: ${(props) => props.color};
  border: 1px solid ${(props) => props.color};
  color: #fff;
  overflow: hidden;

  /* transition: all 0.1s linear; */
  cursor: pointer;
  &:hover {
    border: 1px solid white;
    box-shadow: 0 2px 5px
      ${(props) => transparentize(0.6, darken(0.1, props.color))};
  }
  &.act {
    box-shadow: 0 2px 5px ${(props) => transparentize(0.6, props.color)};
    &:after {
      content: 'L';
      display: block;
      transform: rotateZ(45deg) rotateY(180deg);
      font-size: 12px;
    }
  }
`;
const MiniCPlus = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 3px;
  overflow: hidden;
  width: ${(props) => parseInt(props.size) * 1.2}px;
  height: ${(props) => props.size};
  border-radius: ${(props) => props.theme.PrimaryRadius};
  background: #fff;
  border: 1px solid ${(props) => props.theme.PrimaryBorderColor};
  /* transition: ${(props) => props.theme.PrimaryTransition}; */
  cursor: pointer;
  color: ${(props) => darken(0.1, props.theme.PrimaryBorderColor)};
  &:hover {
    box-shadow: 0 2px 5px
      ${(props) => transparentize(0.2, props.theme.PrimaryBorderColor)};
  }
`;
export const FlexiColor = (props) => {
  const [themeColor, setThemeColor] = useState(props.color);
  const [moreColor, setMoreColor] = useState(false);
  useEffect(() => {
    props.handleChange(themeColor);
  }, [themeColor, props]);
  const node = useRef();
  const handleClick = (e) => {
    if (node.current && node.current.contains(e.target)) {
      return;
    }
    setMoreColor(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  });

  return (
    <ColorWrapper ref={node} size={props.size}>
      <MiniSelected color={themeColor} size={props.size} />
      {props.colors.map((elem) => (
        <MiniColor
          key={`color-swatch-${elem}`}
          color={elem}
          size={props.size}
          className={themeColor === elem ? 'act' : ''}
          onClick={() => setThemeColor(elem)}
        />
      ))}
      <MiniCPlus size={props.size} onClick={() => setMoreColor(!moreColor)}>
        +
      </MiniCPlus>
      {moreColor && (
        <div className="colorshow">
          <SwatchesPicker
            color={themeColor}
            onChangeComplete={(color) => setThemeColor(color.hex)}
          />
        </div>
      )}
    </ColorWrapper>
  );
};

FlexiColor.defaultProps = {
  size: '30px',
  colors: [
    Theme.PrimaryColor,
    '#5184eb',
    '#38c2ff',
    '#2aead4',
    '#90e3ba',
    '#59d973',
    '#a4d013',
    '#ef9822',
    '#ef6122',
    '#ea2a8a',
    '#f450f4',
    '#c28ffc',
    '#989898',
  ],
};

FlexiColor.propTypes = {
  colors: PropTypes.array,
  color: PropTypes.string,
  size: PropTypes.string,
  handleChange: PropTypes.func,
};

export const DottedLine = styled.div`
  height: 0;
  border-top: 1px dashed ${(props) => props.theme.PrimaryBorderColor};
`;

export const ColorBox = styled(Box)`
  & .tred {
    color: ${(props) => props.theme.PrimaryRed};
  }
  & .tgreen {
    color: ${(props) => props.theme.PrimaryGreen};
  }
  & .tblue {
    color: ${(props) => props.theme.PrimaryBlue};
  }
  & .tcolor {
    color: ${(props) => props.theme.PrimaryColor};
  }
  & .tyellow {
    color: ${(props) => props.theme.PrimaryYellow};
  }
  & .tgrey {
    color: ${(props) => props.theme.PrimaryGrey};
  }
`;

export const FilterButton = styled(Button)`
  span {
    &:first-child {
      justify-content: flex-start;
    }
  }
`;

export const CourseViewWrapper = styled(Box)`
  & .layout-grid {
    & .list-view {
      display: none;
    }
  }

  & .layout-list {
    & .grid-view {
      display: none;
    }

    & .list-view {
      margin-top: -4rem;
    }
  }

  & .flexi-table {
    overflow-x: unset;
  }

  & .button-toggle {
    & .active {
      background-color: ${({ theme }) => theme.PrimaryColor};
      color: #fff;
    }
    & .flexi-button {
      &:first-child {
        border-right: 0;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
      &:last-child {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }
`;

export const Timeframer = styled.div`
    & .flexi-tag-center {
      display: block;
    }
`;

export const IconColor = styled.div`
    & .danger {
      color: ${({ theme }) => theme.PrimaryRed};
    }

`
